/**
 * PlayersSection - Players section for scroll wizard
 */
import React, { useState, useEffect, useRef } from 'react';
import { useTournament } from '../../contexts/TournamentContext';
import { useToast } from '../../contexts/ToastContext';

/**
 * Normalise le tableau de joueurs au format "tous les gauches d'abord, puis tous les droits".
 * Remet à null les fixedPartnerId invalides (paire de même position).
 */
export const normalizePlayers = (rawPlayers, playerCount) => {
  const pairCount = playerCount / 2;

  // Déjà dans le bon format ?
  const isAlreadyNormalized =
    rawPlayers.slice(0, pairCount).every(p => p.position === 'left') &&
    rawPlayers.slice(pairCount).every(p => p.position === 'right');
  if (isAlreadyNormalized) return rawPlayers;

  // Réordonner : tous les gauches d'abord, puis tous les droits
  const leftPlayers = rawPlayers.filter(p => p.position === 'left');
  const rightPlayers = rawPlayers.filter(p => p.position === 'right');
  const normalized = [...leftPlayers, ...rightPlayers];

  // Correspondance ancien index → nouvel index
  const oldToNew = new Map(rawPlayers.map((p, oldIdx) => [oldIdx, normalized.indexOf(p)]));

  // Mettre à jour les références fixedPartnerId
  return normalized.map(p => {
    if (p.fixedPartnerId !== null) {
      const partner = rawPlayers[p.fixedPartnerId];
      if (partner && partner.position !== p.position) {
        // Paire valide (positions opposées) : remapper l'index
        return { ...p, fixedPartnerId: oldToNew.get(p.fixedPartnerId) };
      } else {
        // Paire invalide (même position) : effacer
        return { ...p, fixedPartnerId: null };
      }
    }
    return p;
  });
};

const PlayersSection = () => {
  const { config, players: savedPlayers, setPlayers: savePlayers } = useTournament();
  const { showError } = useToast();
  const [players, setPlayers] = useState([]);
  const [fixedPairs, setFixedPairs] = useState(new Set());
  const [initialized, setInitialized] = useState(false);
  const [expandedPairs, setExpandedPairs] = useState(new Set(window.innerWidth <= 768 ? [0] : []));
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const isLocalUpdateRef = useRef(false);
  const previousValueRef = useRef({});

  useEffect(() => {
    if (config && !initialized) {
      initializePlayers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, initialized]);

  // Re-initialize players when playerCount changes (e.g. user changes from 8 to 12)
  useEffect(() => {
    if (config && initialized && config.playerCount !== players.length) {
      updatePlayersCount(config.playerCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.playerCount, initialized]);

  // Detect window resize for mobile/desktop
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Re-sync player names when Firebase data arrives (e.g. after F5 reload)
  useEffect(() => {
    if (isLocalUpdateRef.current) {
      isLocalUpdateRef.current = false;
      return;
    }
    if (!config || !savedPlayers || savedPlayers.length !== config.playerCount) return;

    const pairCount = config.playerCount / 2;
    const normalized = normalizePlayers(savedPlayers, config.playerCount);
    setPlayers([...normalized]);

    // Si la normalisation a modifié les données, mettre à jour aussi le contexte
    // (évite que le scheduler reçoive des fixedPartnerId invalides)
    if (normalized !== savedPlayers) {
      isLocalUpdateRef.current = true;
      savePlayers(normalized);
    }

    const newFixedPairs = new Set();
    for (let i = 0; i < normalized.length; i++) {
      if (normalized[i].fixedPartnerId !== null) {
        const pairIndex = i < pairCount ? i : i - pairCount;
        newFixedPairs.add(pairIndex);
      }
    }
    setFixedPairs(newFixedPairs);
  }, [savedPlayers, config, savePlayers]);


  const updatePlayersCount = (newPlayerCount) => {
    const newPairCount = newPlayerCount / 2;
    const currentPlayers = [...players];

    // Separate current players by position
    const currentLeft = currentPlayers.filter(p => p.position === 'left');
    const currentRight = currentPlayers.filter(p => p.position === 'right');

    // Build new player list: all left first, then all right
    const newPlayers = [];

    // Left players: keep existing, add new if needed, trim if excess
    for (let i = 0; i < newPairCount; i++) {
      if (i < currentLeft.length) {
        newPlayers.push({ ...currentLeft[i], fixedPartnerId: null });
      } else {
        newPlayers.push({ name: `G${i + 1}`, position: 'left', fixedPartnerId: null });
      }
    }

    // Right players: keep existing, add new if needed, trim if excess
    for (let i = 0; i < newPairCount; i++) {
      if (i < currentRight.length) {
        newPlayers.push({ ...currentRight[i], fixedPartnerId: null });
      } else {
        newPlayers.push({ name: `D${i + 1}`, position: 'right', fixedPartnerId: null });
      }
    }

    setPlayers(newPlayers);
    setFixedPairs(new Set());
    isLocalUpdateRef.current = true;
    savePlayers(newPlayers);
  };

  const initializePlayers = () => {
    const playerCount = config.playerCount;
    const pairCount = playerCount / 2;
    const newPlayers = [];
    const newFixedPairs = new Set();

    // Check if we have existing players to restore
    if (savedPlayers && savedPlayers.length === playerCount) {
      // Normalize player order: all left first, then all right (fixes legacy alternating format)
      const normalized = normalizePlayers(savedPlayers, playerCount);
      newPlayers.push(...normalized);

      // Restore fixed pairs from normalized data
      for (let i = 0; i < normalized.length; i++) {
        if (normalized[i].fixedPartnerId !== null) {
          const pairIndex = i < pairCount ? i : i - pairCount;
          newFixedPairs.add(pairIndex);
        }
      }
    } else {
      // Create new players with default names
      for (let pairIndex = 0; pairIndex < pairCount; pairIndex++) {
        // Left player
        newPlayers.push({
          name: `G${pairIndex + 1}`,
          position: 'left',
          fixedPartnerId: null
        });
      }
      for (let pairIndex = 0; pairIndex < pairCount; pairIndex++) {
        // Right player
        newPlayers.push({
          name: `D${pairIndex + 1}`,
          position: 'right',
          fixedPartnerId: null
        });
      }
    }

    setPlayers(newPlayers);
    setFixedPairs(newFixedPairs);

    // Save to context only on first initialization
    if (!initialized) {
      isLocalUpdateRef.current = true;
      savePlayers(newPlayers);
      setInitialized(true);
    }
  };

  const handlePlayerNameChange = (index, name) => {
    const newPlayers = [...players];
    newPlayers[index] = {
      ...newPlayers[index],
      name: name
    };
    setPlayers(newPlayers);
    // Auto-save to context
    isLocalUpdateRef.current = true;
    savePlayers(newPlayers);
  };

  const handleFixedPairToggle = (pairIndex) => {
    const newFixedPairs = new Set(fixedPairs);
    const playerCount = config.playerCount;
    const pairCount = playerCount / 2;
    const leftIndex = pairIndex;
    const rightIndex = pairIndex + pairCount;

    if (newFixedPairs.has(pairIndex)) {
      // Remove fixed pair
      newFixedPairs.delete(pairIndex);
      const newPlayers = [...players];
      newPlayers[leftIndex] = { ...newPlayers[leftIndex], fixedPartnerId: null };
      newPlayers[rightIndex] = { ...newPlayers[rightIndex], fixedPartnerId: null };
      setPlayers(newPlayers);
      isLocalUpdateRef.current = true;
      savePlayers(newPlayers);
    } else {
      // Add fixed pair
      newFixedPairs.add(pairIndex);
      const newPlayers = [...players];
      newPlayers[leftIndex] = { ...newPlayers[leftIndex], fixedPartnerId: rightIndex };
      newPlayers[rightIndex] = { ...newPlayers[rightIndex], fixedPartnerId: leftIndex };
      setPlayers(newPlayers);
      isLocalUpdateRef.current = true;
      savePlayers(newPlayers);
    }

    setFixedPairs(newFixedPairs);
  };

  const handleInputFocus = (e, index) => {
    e.target.select();
    previousValueRef.current[index] = players[index]?.name || '';
  };

  const handlePlayerNameBlur = (index, currentName) => {
    const trimmed = currentName.trim();
    const isDuplicate = players.some((p, i) =>
      i !== index && p.name.trim().toLowerCase() === trimmed.toLowerCase()
    );

    if (isDuplicate) {
      const previousName = previousValueRef.current[index] || '';
      const newPlayers = [...players];
      newPlayers[index] = { ...newPlayers[index], name: previousName };
      setPlayers(newPlayers);
      isLocalUpdateRef.current = true;
      savePlayers(newPlayers);
      showError(`Le nom "${trimmed}" est déjà utilisé par un autre joueur.`);
    }
  };

  const togglePairExpansion = (pairIndex) => {
    const newExpanded = new Set(expandedPairs);
    if (newExpanded.has(pairIndex)) {
      newExpanded.delete(pairIndex);
    } else {
      newExpanded.add(pairIndex);
    }
    setExpandedPairs(newExpanded);
  };

  if (!config) {
    return (
      <div className="section-inner">
        <div className="section-header">
          <h1>
            <i className="bi bi-people-fill me-3"></i>
            Joueurs
          </h1>
        </div>
        <div className="section-body">
          <p className="text-center text-muted">Veuillez d'abord configurer le tournoi.</p>
        </div>
      </div>
    );
  }

  const playerCount = config.playerCount;
  const pairCount = playerCount / 2;

  return (
    <div className="section-inner">
      <div className="section-header">
        <h1>
          <i className="bi bi-people-fill me-3"></i>
          Joueurs
        </h1>
      </div>

      <div className="section-body">
        <div className="players-list">
          {Array.from({ length: pairCount }, (_, pairIndex) => {
            const leftIndex = pairIndex;
            const rightIndex = pairIndex + pairCount;
            const isFixedPair = fixedPairs.has(pairIndex);

            const isExpanded = expandedPairs.has(pairIndex);
            const leftPlayer = players[leftIndex];
            const rightPlayer = players[rightIndex];
            const showContent = !isMobile || isExpanded; // Desktop: always show, Mobile: only if expanded

            return (
              <div
                key={pairIndex}
                className="player-pair-row mb-3 border rounded"
                data-pair-index={pairIndex}
              >
                {/* Avatar Header - Clickable only on mobile */}
                <div
                  className="pair-header"
                  onClick={isMobile ? () => togglePairExpansion(pairIndex) : undefined}
                  style={{ cursor: isMobile ? 'pointer' : 'default' }}
                >
                  <div className="pair-avatar">
                    <span className="pair-number">#{pairIndex + 1}</span>
                  </div>
                  {isMobile && !isExpanded ? (
                    <div className="pair-names-collapsed">
                      <span className="player-name-collapsed">
                        {leftPlayer?.name || `G${pairIndex + 1}`}
                      </span>
                      <span className="separator-collapsed">/</span>
                      <span className="player-name-collapsed">
                        {rightPlayer?.name || `D${pairIndex + 1}`}
                      </span>
                    </div>
                  ) : (
                    <div className="pair-header-spacer"></div>
                  )}
                  {/* Fixed Pair Checkbox - Visible only when expanded */}
                  {showContent && (
                    <div className="form-check" onClick={(e) => e.stopPropagation()}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`fixed-pair-${pairIndex}`}
                        checked={isFixedPair}
                        onChange={() => handleFixedPairToggle(pairIndex)}
                        title="Paire fixe"
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`fixed-pair-${pairIndex}`}
                        title="Paire fixe"
                      >
                        <i className="bi bi-link-45deg"></i> Paire fixe
                      </label>
                    </div>
                  )}
                </div>

                {/* Content - Always visible on desktop, collapsible on mobile */}
                {showContent && (
                  <div className="pair-content">
                    <div className="d-flex align-items-center gap-3">
                      {/* Left Player */}
                      <div className="player-card-inline d-flex align-items-center gap-2 flex-grow-1">
                        <span className="badge bg-primary d-flex align-items-center gap-1">
                          <i className="bi bi-arrow-left-circle"></i> G
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          id={`player-${leftIndex}`}
                          value={leftPlayer?.name || ''}
                          onChange={(e) => handlePlayerNameChange(leftIndex, e.target.value)}
                          onFocus={(e) => handleInputFocus(e, leftIndex)}
                          onBlur={(e) => handlePlayerNameBlur(leftIndex, e.target.value)}
                          required
                          maxLength={30}
                          autoComplete="off"
                        />
                      </div>

                      {/* Separator */}
                      <div className="pair-separator text-muted fw-bold px-2">&</div>

                      {/* Right Player */}
                      <div className="player-card-inline d-flex align-items-center gap-2 flex-grow-1">
                        <span className="badge bg-danger d-flex align-items-center gap-1">
                          D <i className="bi bi-arrow-right-circle"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          id={`player-${rightIndex}`}
                          value={rightPlayer?.name || ''}
                          onChange={(e) => handlePlayerNameChange(rightIndex, e.target.value)}
                          onFocus={(e) => handleInputFocus(e, rightIndex)}
                          onBlur={(e) => handlePlayerNameBlur(rightIndex, e.target.value)}
                          required
                          maxLength={30}
                          autoComplete="off"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PlayersSection;
